import { Component, input, output, signal, computed, inject, LOCALE_ID, OnInit, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { CartService } from '../../../../../../services/cart/cart';
import { PharmacistCartService } from '../../../../../../services/cart/cart.pharmacist';
import { EnrichedOfferItem } from '../available-offers/available-offers';
import {
  OfferDetails,
  OfferProduct,
  SelectedOfferItem,
  AddCartItemsDTO,
  CartDto,
} from '../../../../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { Loading } from '../../../../../../utils/loading/loading';
import { Stepper } from '../../../../../../shared/ui/stepper/stepper';
import { GiftBoxIcon } from '../../../../../../shared/ui/icons/gift-box-icon/gift-box-icon';
import { ShoppingCartIcon } from '../../../../../../shared/ui/icons/shopping-cart-icon/shopping-cart-icon';
import { SearchIcon } from '../../../../../../shared/ui/icons/search-icon/search-icon';
import { TrashIcon } from '../../../../../../shared/ui/icons/trash-icon/trash-icon';
import { ImagePlaceholderIcon } from '../../../../../../shared/ui/icons/image-placeholder-icon/image-placeholder-icon';
import { BatchNumberIcon } from '../../../../../../shared/ui/icons/product-card/batch-number-icon/batch-number-icon';
import { MfgExpDatesIcon } from '../../../../../../shared/ui/icons/product-card/mfg-exp-dates-icon/mfg-exp-dates-icon';
import { CustomerPriceIcon } from '../../../../../../shared/ui/icons/product-card/customer-price-icon/customer-price-icon';
import { BusinessPriceIcon } from '../../../../../../shared/ui/icons/product-card/business-price-icon/business-price-icon';
import { ProductsFileMgt } from '../../../../../../services/product/file-management/products.file-mgt.service';
import { SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-offer-product-selector',
  standalone: true,
  imports: [
    CommonModule,
    Loading,
    Stepper,
    GiftBoxIcon,
    ShoppingCartIcon,
    SearchIcon,
    TrashIcon,
    ImagePlaceholderIcon,
    BatchNumberIcon,
    MfgExpDatesIcon,
    CustomerPriceIcon,
    BusinessPriceIcon,
  ],
  templateUrl: './offer-product-selector.html',
})
export class OfferProductSelector implements OnInit {
  private locale = inject(LOCALE_ID);
  private cartService = inject(CartService);
  private pharmacistCartService = inject(PharmacistCartService);
  private fileMgt = inject(ProductsFileMgt);
  private destroyRef = inject(DestroyRef);

  offer = input.required<EnrichedOfferItem>();
  disabled = input<boolean>(false);

  mode = input<'admin' | 'pharmacist'>('admin');
  cartId = input<number | undefined>(undefined);
  pharmacyId = input<number | undefined>(undefined);

  cancel = output<void>();
  confirmOffer = output<{ offerId: number; quantity: number; selectedProducts: SelectedOfferItem[] }>();

  offerDetails = signal<OfferDetails | null>(null);
  searchQuery = signal<string>('');
  loadingProducts = signal<boolean>(false);
  selectedItems = signal<SelectedOfferItem[]>([]);
  showMaxLimitError = signal<boolean>(false);

  offerSelectedQuantity = signal<number>(1);

  isAdding = signal<boolean>(false);
  addError = signal<string>('');

  imagePreviews = signal<Record<string, string | SafeUrl>>({});

  isArabic = computed(() => this.locale.startsWith('ar'));

  totalRequiredCount = computed(() => {
    const details = this.offerDetails();
    if (!details) return 0;
    return this.offerSelectedQuantity() * (details.buyQuantity + details.getQuantity);
  });

  currentSelectedCount = computed(() => {
    return this.selectedItems()?.reduce((acc: number, item: SelectedOfferItem) => acc + item.selectedQty, 0);
  });

  progressPercentage = computed(() => {
    const req = this.totalRequiredCount();
    if (req === 0) return 0;
    return Math.min(100, (this.currentSelectedCount() / req) * 100);
  });

  isLimitReached = computed(() => this.currentSelectedCount() >= this.totalRequiredCount());

  availableProducts = computed(() => {
    const details = this.offerDetails();
    if (!details) return [];

    const unselectedProducts = details.products.filter((p: OfferProduct) => !this.isProductSelected(p.id));

    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return unselectedProducts;
    return unselectedProducts.filter(
      (p: OfferProduct) =>
        p.titleEn.toLowerCase().includes(query) ||
        p.titleAr.includes(query) ||
        (p.batchNumber && p.batchNumber.includes(query)),
    );
  });

  regularTotal = computed(() => {
    return this.selectedItems()?.reduce(
      (acc: number, item: SelectedOfferItem) => acc + item.businessPrice * item.selectedQty,
      0,
    );
  });

  offerTotal = computed(() => {
    const details = this.offerDetails();
    if (!details) return this.regularTotal();

    const flatList: number[] = [];
    this.selectedItems().forEach((item: SelectedOfferItem) => {
      for (let i = 0; i < item.selectedQty; i++) {
        flatList.push(item.businessPrice);
      }
    });

    flatList.sort((a, b) => a - b);
    const totalItemsCount = flatList.length;
    const calculatedTotalRequired = this.totalRequiredCount();
    const totalFreeCount = this.offerSelectedQuantity() * details.getQuantity;

    if (totalItemsCount < calculatedTotalRequired) {
      return this.regularTotal();
    }

    const paidItems = flatList.slice(totalFreeCount);
    return paidItems?.reduce((acc: number, price: number) => acc + price, 0);
  });

  ngOnInit() {
    this.offerSelectedQuantity.set(this.offer().selectedQuantity || 1);
    this.loadOfferFullDetails();
  }

  private loadOfferFullDetails() {
    this.loadingProducts.set(true);
    const subscription = this.cartService.getOfferDetails(this.offer().id).subscribe({
      next: (res: OfferDetails) => {
        this.offerDetails.set(res);
        this.loadingProducts.set(false);
        this.preloadProductImages(res.products);
      },
      error: () => {
        this.loadingProducts.set(false);
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  private preloadProductImages(products: OfferProduct[]) {
    products.forEach((p: any) => {
      const fid = p.profileImageFid;
      if (fid) this.resolveProductImage(fid);
    });
  }

  private resolveProductImage(fid: string) {
    if (!fid || this.imagePreviews()[fid]) return;
    const subscription = this.fileMgt.getImageUrl(fid).subscribe({
      next: (res) => {
        this.imagePreviews.update((map) => ({
          ...map,
          [fid]: res.isImage ? res.url : '/assets/product.png',
        }));
      },
      error: () => {
        this.imagePreviews.update((map) => ({
          ...map,
          [fid]: '/assets/product.png',
        }));
      },
    });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  onSearchInput(event: Event) {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  increaseOfferQty() {
    this.offerSelectedQuantity.update((v) => v + 1);
  }

  decreaseOfferQty() {
    this.offerSelectedQuantity.update((v) => Math.max(1, v - 1));
  }

  onOfferQtyChange(value: string) {
    const num = +value;
    this.offerSelectedQuantity.set(!num || num < 1 ? 1 : num);
  }

  isProductSelected(productId: number): boolean {
    return this.selectedItems().some((i: SelectedOfferItem) => i.id === productId);
  }

  getProductQtyInsideOffer(productId: number): number {
    const found = this.selectedItems().find((i: SelectedOfferItem) => i.id === productId);
    return found ? found.selectedQty : 0;
  }

  addProductToOffer(product: OfferProduct) {
    this.showMaxLimitError.set(false);
    if (this.currentSelectedCount() >= this.totalRequiredCount()) {
      this.showMaxLimitError.set(true);
      return;
    }

    this.selectedItems.update((prev: SelectedOfferItem[]) => {
      const existing = prev.find((i: SelectedOfferItem) => i.id === product.id);
      if (existing) {
        return prev.map((i: SelectedOfferItem) => (i.id === product.id ? { ...i, selectedQty: i.selectedQty + 1 } : i));
      }
      return [
        ...prev,
        {
          id: product.id,
          titleEn: product.titleEn,
          titleAr: product.titleAr,
          businessPrice: product.businessPrice,
          customerPrice: product.customerPrice,
          selectedQty: 1,
        },
      ];
    });
  }

  updateProductQtyInsideOffer(productId: number, delta: number) {
    this.showMaxLimitError.set(false);

    if (delta > 0 && this.currentSelectedCount() >= this.totalRequiredCount()) {
      this.showMaxLimitError.set(true);
      return;
    }

    this.selectedItems.update((prev: SelectedOfferItem[]) => {
      return prev
        .map((item: SelectedOfferItem) => {
          if (item.id !== productId) return item;
          const nextQty = item.selectedQty + delta;
          return nextQty > 0 ? { ...item, selectedQty: nextQty } : null;
        })
        .filter((item): item is SelectedOfferItem => item !== null);
    });
  }

  removeProductFromOffer(productId: number) {
    this.showMaxLimitError.set(false);
    this.selectedItems.update((prev: SelectedOfferItem[]) => prev.filter((i: SelectedOfferItem) => i.id !== productId));
  }

  confirmOfferSelection() {
    if (this.currentSelectedCount() !== this.totalRequiredCount()) return;

    const offerId = this.offer().id;
    const quantity = this.offerSelectedQuantity();
    const selectedProducts = this.selectedItems();

    this.addError.set('');
    this.isAdding.set(true);

    if (this.mode() === 'admin') {
      const cartId = this.cartId();
      if (!cartId) {
        this.isAdding.set(false);
        this.addError.set(this.isArabic() ? 'لا يوجد رقم عربة' : 'Missing cart id');
        return;
      }

      const dto: AddCartItemsDTO = {
        items: [
          {
            type: 'offer',
            offerId,
            quantity,
            products: selectedProducts.map((p) => ({ customerProductId: p.id, quantity: p.selectedQty })),
          },
        ],
      };

      const subscription = this.cartService
        .addItemsToCart(cartId, dto)
        .pipe(finalize(() => this.isAdding.set(false)))
        .subscribe({
          next: () => this.confirmOffer.emit({ offerId, quantity, selectedProducts }),
          error: (err) => {
            this.addError.set(
              this.isArabic() ? err.messageAr || 'حدث خطأ أثناء إضافة العرض' : err.messageEn || 'Failed to add offer',
            );
          },
        });
      this.destroyRef.onDestroy(() => subscription.unsubscribe());
    } else {
      const pharmacyId = this.pharmacyId();
      if (!pharmacyId) {
        this.isAdding.set(false);
        this.addError.set(this.isArabic() ? 'لا يوجد رقم صيدلية' : 'Missing pharmacy id');
        return;
      }

      const dto: CartDto = {
        items: [
          {
            type: 'offer',
            offerId,
            products: selectedProducts.map((p) => ({ customerProductId: p.id, quantity: p.selectedQty })),
          },
        ],
      };

      const subscription = this.pharmacistCartService
        .addItemToCart('offer', pharmacyId, dto)
        .pipe(finalize(() => this.isAdding.set(false)))
        .subscribe({
          next: () => this.confirmOffer.emit({ offerId, quantity, selectedProducts }),
          error: (err) => {
            this.addError.set(
              this.isArabic() ? err.messageAr || 'حدث خطأ أثناء إضافة العرض' : err.messageEn || 'Failed to add offer',
            );
          },
        });
      this.destroyRef.onDestroy(() => subscription.unsubscribe());
    }
  }
}
